import { ChecklightSessionStatus } from '@thesavingsgroup/enums/ChecklightSessionStatus';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import { find, get, isEqual } from 'lodash';
import { Fragment, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import LogoDefault from '../../../../../assets/logo-default.svg';
import { UserVehicle } from '../../../../../common/interfaces/vehicle/vehicle.interface';
import { ApplicationTasks } from '../../../../../components/ApplicationTasks/ApplicationTasks';
import { ApplicationUpdated } from '../../../../../components/shared/ApplicationUpdated/ApplicationUpdated';
import { ErrorsBlock } from '../../../../../components/shared/ErrorsBlock/ErrorsBlock';
import { VehiclePageHeader } from '../../../../../components/shared/PageHeader/VehiclePageHeader';
import { config } from '../../../../../config';
import { Workflow } from '../../../../../constants/Workflow';
import { useAPI } from '../../../../../hooks/useAPI/useAPI';
import { useCurrentUserContext } from '../../../../../hooks/useCurrentUserContext/useCurrentUserContext';
import { useModal } from '../../../../../hooks/useModal/useModal';
import { usePollWhileQueuedForSubmission } from '../../../../../hooks/usePollWhileQueuedForSubmission/usePollWhileQueuedForSubmission';
import { useWorkflowPathname } from '../../../../../hooks/useWorkflowPathname/useWorkflowPathname';
import { getAllApplicationTasks } from '../../../../../services/status/statusService';
import useSubmissionStatusStore from '../../../../../store/_legacy/submissionStatusStore/useSubmissionStatusStore';
import useAnalyticStore from '../../../../../store/analyticStore/useAnalyticStore';
import { ApplicationDataInterface } from '../../applicationData.interface';
import ApplicationResources from './ApplicationResources';
import { ContentBlock, ContentTitle, ContentTitleContainer } from './styles';

export const ViewApplication = ({
  applicationData,
  availableTasks,
  fetchVehicleError,
  openSummary,
}: {
  applicationData: ApplicationDataInterface;
  availableTasks: any;
  fetchVehicleError: any;
  openSummary: boolean;
}) => {
  const navigate = useNavigate();
  const { isShowing, open } = useModal();
  const [currentUserContext, setCurrentUserContext] = useCurrentUserContext();
  const location = useLocation();
  const api = useAPI();

  const { retryFailedSubmissionError, setRetryFailedSubmissionError } =
    useSubmissionStatusStore();

  const { sendApplicationViewedAnalytics } = useAnalyticStore();

  const { currentVehicle } = applicationData || {};

  const vehicleTitle = [
    currentVehicle.year,
    currentVehicle.make,
    currentVehicle.model,
  ]
    .filter(isNonEmptyString)
    .join(' ');

  const checklightSession = get(currentVehicle, 'checklightSession');

  const applicationStatus = get(checklightSession, 'status');
  const autopayNumber = get(checklightSession, 'autopayNumber');

  const applicationInSubmission =
    applicationStatus === ChecklightSessionStatus.QUEUED_FOR_SUBMISSION ||
    applicationStatus === ChecklightSessionStatus.FAILED_SUBMISSION;

  const isQueuedForSubmission =
    applicationStatus === ChecklightSessionStatus.QUEUED_FOR_SUBMISSION;

  const workflowName = useWorkflowPathname();

  // See: usePollWhileQueuedForSubmission.ts for more details
  const getCurrentVehicleStatus = (): ChecklightSessionStatus | undefined => {
    if (!currentUserContext || !currentVehicle) {
      return undefined;
    }
    const vehicle = find(
      currentUserContext.userVehicles,
      (vehicle) => vehicle.id === currentVehicle.id,
    );

    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    return get(vehicle, 'checklightSessions.0.status');
  };

  // See: usePollWhileQueuedForSubmission.ts for more details
  const fetchUserVehicles = async () => {
    const data = await api.get<any, UserVehicle[]>('/vehicles');

    if (!isEqual(data, currentUserContext?.userVehicles)) {
      setCurrentUserContext({ ...currentUserContext, userVehicles: data });
    }

    return data;
  };

  const onStatusChange = (userVehicles: UserVehicle[]) => {
    // Find the current vehicle in the list of updated user vehicles.
    const updatedVehicle = find(
      userVehicles,
      (vehicle) => vehicle.id === currentVehicle.id,
    );

    if (updatedVehicle) {
      open();
    }
  };

  usePollWhileQueuedForSubmission({
    shouldPoll: isQueuedForSubmission,
    fetchUserVehicles,
    getCurrentVehicleStatus,
    onStatusChange,
  });

  useEffect(() => {
    // Cleaning up failed state when the user navigates away from the page.
    setRetryFailedSubmissionError(false);
  }, [location.pathname]);

  useEffect(() => {
    // Send analytics for application viewed
    sendApplicationViewedAnalytics(applicationStatus);
  }, []);

  const applicationPageContent = (
    <Fragment>
      <ContentBlock>
        <ContentTitleContainer>
          <ContentTitle>Next task</ContentTitle>
        </ContentTitleContainer>
        {isShowing && <ApplicationUpdated />}
        {retryFailedSubmissionError && (
          <div style={{ marginBottom: '1.5rem' }}>
            <ErrorsBlock
              errorsList={[
                'An unexpected exception occurred. Please try again later.',
              ]}
            />
          </div>
        )}
        <ApplicationTasks
          availableTasks={availableTasks}
          state={applicationData}
          type="application"
        />
      </ContentBlock>

      {config.debug.gates &&
      workflowName === Workflow.unified &&
      !applicationInSubmission ? (
        <ContentBlock>
          <ContentTitle>All tasks (for testing only)</ContentTitle>
          <ApplicationTasks
            availableTasks={getAllApplicationTasks(workflowName)}
            state={applicationData}
            type="application"
            isList
          />
        </ContentBlock>
      ) : null}
      <ApplicationResources
        applicationData={applicationData}
        openSummary={openSummary}
      />
    </Fragment>
  );

  return (
    <Fragment>
      <VehiclePageHeader
        breadCrumbs={autopayNumber ? [autopayNumber] : []}
        image={
          currentVehicle.imageList?.find(
            (image: any) => image.vehicleImageType === 'DEFAULT',
          )?.url
        }
        logo={applicationData?.branding?.logo?.url || LogoDefault}
        navigate={navigate}
        title={vehicleTitle}
      />
      {fetchVehicleError ? (
        <ErrorsBlock
          errorsList={[
            'An unexpected exception occurred while attempting load the users vehicle details',
          ]}
        />
      ) : (
        applicationPageContent
      )}
    </Fragment>
  );
};
